import React, {FC, useEffect, useState} from 'react';
import { useTranslation } from 'react-i18next';
import {useAppDispatch} from "../../../../hooks/useAppDispatch";
import {setConvertedGeometry, setUploadedFiles} from "../../../../services/reducers/utilities-journal";
import {setConvertedFeatures} from "../../../../services/actions/map";
import {convertGeometryToArcgis} from "../../../../services/api";
import {useAppSelector} from "../../../../hooks/useAppSelector";
import {StageType} from "../../../../interfaces/utilities";
import ImportFileModalView, {
    isAllowedExtension
} from "../../../../shared/import-file-modal-view/import-file-modal-view";

interface ImportFileModalProps {
    onClose: () => void;
    stage: StageType
}

const EXT_FILES = ['shp','shx','dbf']

const ImportFileModal: FC<ImportFileModalProps> = ({ onClose, stage }) => {
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [selectedCoordinateSystem, setSelectedCoordinateSystem] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const { uploadedFiles } = useAppSelector(state => state.masterUtilities)
    const [error, setError] = useState<string | null>(null);
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (error) {
            setError(null);
        }
    }, [selectedFiles, selectedCoordinateSystem]);

    const processFiles = (files: File[]) => {
        const validFiles = files.filter((file) => {
            return isAllowedExtension(file, EXT_FILES);
        });
        const invalidFiles = files.filter(file => !isAllowedExtension(file, EXT_FILES));

        if (invalidFiles.length > 0) {
            const names = invalidFiles.map(f => f.name).join(', ');
            setError(t('import-file-modal.unsupportedFormat', { names }));
            return;
        }

        const allUploaded = uploadedFiles[stage] ?? [];

        const existing = new Set([
            ...selectedFiles.map(f => f.name),
            ...allUploaded.map(f => f.extension ? `${f.name}.${f.extension}` : f.name),
        ]);

        const unique = validFiles.filter(f => !existing.has(f.name));
        const dupes = validFiles.filter(f => existing.has(f.name));

        if (dupes.length > 0) {
            const names = dupes.map(d => d.name).join(', ');
            setError(
                t(dupes.length === 1 ? 'import-file-modal.fileAlreadyAddedOne' : 'import-file-modal.fileAlreadyAddedMany', { names })
            );
            return;
        } else {
            setError(null);
        }

        if (unique.length > 0) {
            setSelectedFiles(prev => [...prev, ...unique]);
        }
    };

    const handleSubmit = async () => {
        if (selectedFiles.length === 0 || selectedCoordinateSystem === null) return;

        const requiredExt = ['shp', 'shx', 'dbf'];

        const filesByBase: Record<string, Set<string>> = {};
        for (const file of selectedFiles) {
            const parts = file.name.split('.');
            const ext = parts.pop()!.toLowerCase();
            const base = parts.join('.');
            if (!filesByBase[base]) filesByBase[base] = new Set();
            filesByBase[base].add(ext);
        }

        const errors: string[] = [];
        for (const [base, exts] of Object.entries(filesByBase)) {
            const missing = requiredExt.filter(e => !exts.has(e));
            if (missing.length > 0) {
                const list = missing.map(e => `.${e}`).join(', ');
                errors.push(t('import-file-modal.missingFiles', { base, list }));
            }
        }

        if (errors.length > 0) {
            setError(errors.join('; '));
            return;
        }

        try {
            setIsLoading(true);
            const geometryByFile = await convertGeometryToArcgis(
                selectedCoordinateSystem,
                selectedFiles
            );

            const geometryByFileNoExt: Record<string, any> = {};
            for (const fileObj of geometryByFile) {
                const nameWithoutExt = fileObj.fileName.replace(/\.[^/.]+$/, "");
                geometryByFileNoExt[nameWithoutExt] = fileObj;
            }

            dispatch(setConvertedGeometry({stage, geometry: geometryByFileNoExt}));

            const filesToUpload = selectedFiles
                .filter(file => file.name.toLowerCase().endsWith('.shp'))
                .map(file => {
                    const parts = file.name.split('.');
                    const extension = parts.length > 1 ? parts.pop()! : '';
                    const name = parts.join('.');
                    return { name, extension };
                });

            dispatch(setUploadedFiles({stage, files: filesToUpload}));
            dispatch(setConvertedFeatures());

            setSelectedFiles([]);
            setSelectedCoordinateSystem(null);
            onClose();
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const removeFile = (fileToRemove: File) => {
        const baseNameToRemove = fileToRemove.name.replace(/\.[^/.]+$/, "");
        setSelectedFiles(prev =>
            prev.filter(file => file.name.replace(/\.[^/.]+$/, "") !== baseNameToRemove)
        );
    };

    const isSubmitDisabled =
        selectedFiles.length === 0 ||
        selectedCoordinateSystem === null ||
        isLoading;

    const { t } = useTranslation();

    return (
        <ImportFileModalView
            onClose={onClose}
            selectedCoordinateSystem={selectedCoordinateSystem}
            onCoordinateSystemChange={setSelectedCoordinateSystem}
            selectedFiles={selectedFiles}
            removeFile={removeFile}
            error={error}
            handleSubmit={handleSubmit}
            isSubmitDisabled={isSubmitDisabled}
            acceptedFileTypes=".shp,.shx,.dbf"
            onFilesSelected={processFiles}
        />
    );
};

export default ImportFileModal;
